import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Shield, ChevronDown, ChevronRight, Layers, FolderOpen, Settings2, CircleDot } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import type { AiFormMetadata } from '@/scripts/userflow';

// Hierarchical permission structure matching super-admin menu features format
export interface HierarchicalPermissions {
  [featureKey: string]: {
    label: string;
    children?: {
      [childKey: string]: string; // childKey -> label
    };
  };
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions: HierarchicalPermissions; // Hierarchical structure
  isActive: boolean;
  isSystem: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleFormData {
  name: string;
  description?: string;
  permissions: HierarchicalPermissions; // Hierarchical structure
  isActive: boolean;
}

export interface Permission {
  id: string;
  label: string;
  category: string;
}

// New hierarchical permission types with nested sub-features
export interface PermissionSubFeature {
  id: string;
  label: string;
}

export interface PermissionFeature {
  id: string;
  label: string;
  children?: PermissionSubFeature[]; // Sub-features (e.g., changeLogo, editGeneral under General)
}

export interface PermissionHierarchyChild {
  id: string;
  label: string;
  path: string;
  features: PermissionFeature[];
}

export interface PermissionHierarchyItem {
  id: string;
  label: string;
  path: string;
  features: PermissionFeature[];
  children: PermissionHierarchyChild[];
}

// AllowedMenu type from AuthContext
interface AllowedMenu {
  id: string;
  label: string;
  path: string;
  children?: AllowedMenu[];
}

// AI Form Metadata for conversational form filling
export const ROLE_FORM_METADATA: AiFormMetadata = {
  formId: 'create_role_form',
  formName: 'Create Role',
  endpoint: '/roles',
  method: 'POST',
  fields: [
    {
      name: 'name',
      label: 'Role Name',
      type: 'text',
      required: true,
      helpText: 'Choose a descriptive name for this role',
      example: 'ESG Data Manager',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      helpText: "Describe this role's responsibilities and what it does in your organization",
      example: 'Responsible for collecting and verifying environmental data',
    },
    {
      name: 'permissions',
      label: 'Permission Level',
      type: 'select',
      required: true,
      helpText: 'Select the permission level for this role',
      options: [
        { value: 'view', label: 'View Only' },
        { value: 'contribute', label: 'Contributor (edit data)' },
        { value: 'manage', label: 'Manager (approve & report)' },
        { value: 'full', label: 'Full Access' },
      ],
    },
  ],
};

interface RoleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RoleFormData) => Promise<void>;
  initialData?: Role | null;
  availablePermissions: Permission[];
  permissionsHierarchy?: PermissionHierarchyItem[];
  allowedMenus?: AllowedMenu[]; // Actual menu paths from user profile
}

const RoleFormModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  availablePermissions,
  permissionsHierarchy,
  allowedMenus,
}: RoleFormModalProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: {},
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  // Track selected permission IDs for UI state (flat array for checkbox state)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Check if current user is an invited user (NOT a tenant-admin)
  // Invited users have invitedBy set (someone invited them)
  const isInvitedUser = !!user?.invitedBy;

  // Filter permissions hierarchy based on user's role permissions
  // Invited users can only see/assign permissions they have been granted
  const filteredPermissionsHierarchy = useMemo(() => {
    if (!permissionsHierarchy || permissionsHierarchy.length === 0) return [];

    // If user is tenant-admin (not invited), show all permissions
    if (!isInvitedUser) {
      return permissionsHierarchy;
    }

    // Get user's role permissions
    const rolePermissions = user?.rolePermissions;
    if (!rolePermissions) return [];

    // Check for fullAccess (Admin role)
    if (rolePermissions.fullAccess) {
      return permissionsHierarchy;
    }

    // Filter hierarchy to only show menus/features the user has access to
    const filtered: PermissionHierarchyItem[] = [];

    permissionsHierarchy.forEach(menu => {
      const menuKey = menu.path.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_');

      // Check if user has permissions for this menu
      const menuPermissions = rolePermissions[menuKey];

      if (menuPermissions) {
        // User has some permissions for this menu - include it
        // Filter children to only those the user has access to
        const filteredChildren: PermissionHierarchyChild[] = [];

        menu.children?.forEach(child => {
          const childMenuKey = child.path.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_');
          const childPermissions = rolePermissions[childMenuKey];

          if (childPermissions) {
            filteredChildren.push(child);
          }
        });

        filtered.push({
          ...menu,
          children: filteredChildren,
        });
      } else {
        // Check if any child menus have permissions
        const filteredChildren: PermissionHierarchyChild[] = [];

        menu.children?.forEach(child => {
          const childMenuKey = child.path.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_');
          const childPermissions = rolePermissions[childMenuKey];

          if (childPermissions) {
            filteredChildren.push(child);
          }
        });

        // If any children have permissions, include the parent menu with those children
        if (filteredChildren.length > 0) {
          filtered.push({
            ...menu,
            features: [], // Parent doesn't have direct permissions
            children: filteredChildren,
          });
        }
      }
    });

    return filtered;
  }, [permissionsHierarchy, isInvitedUser, user?.rolePermissions]);

  // Group permissions by category (for fallback flat view)
  const groupedPermissions = availablePermissions.reduce((acc, permission) => {
    if (!acc[permission.category]) {
      acc[permission.category] = [];
    }
    acc[permission.category].push(permission);
    return acc;
  }, {} as { [key: string]: Permission[] });

  // Check if we have hierarchical permissions
  const hasHierarchy = filteredPermissionsHierarchy && filteredPermissionsHierarchy.length > 0;

  // Build a lookup map from menu label to actual path from allowedMenus
  // This helps us map permission hierarchy labels to actual menu paths for filtering
  const buildMenuLabelToPathMap = useMemo(() => {
    const labelToPath = new Map<string, string>();

    const processMenus = (menus: AllowedMenu[] | undefined) => {
      if (!menus) return;
      menus.forEach(menu => {
        // Normalize label for matching (lowercase, replace spaces/special chars)
        const normalizedLabel = menu.label.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and').replace(/-/g, '_');
        // Convert path to key format: /analytics/overview -> analytics_overview
        const pathKey = menu.path?.replace(/^\//, '').replace(/\//g, '_').replace(/-/g, '_').toLowerCase() || '';

        if (pathKey) {
          labelToPath.set(normalizedLabel, pathKey);
          // Also map by just the menu label without normalization
          labelToPath.set(menu.label.toLowerCase(), pathKey);
        }

        // Process children recursively
        if (menu.children) {
          processMenus(menu.children);
        }
      });
    };

    processMenus(allowedMenus);
    console.log('[RoleFormModal] Label to Path map:', Object.fromEntries(labelToPath));
    return labelToPath;
  }, [allowedMenus]);

  // Build lookup maps for ID to simple key conversion WITH menu context
  // New structure groups by menu: { menuKey: { featureKey: { label, children: { childKey: label } } } }
  const buildLookupMaps = () => {
    // feature ID -> { simpleKey, label, menuKey (simple), menuLabel }
    const featureIdToInfo = new Map<string, { simpleKey: string; label: string; menuKey: string; menuLabel: string }>();
    // child ID -> { simpleKey, label, parentSimpleKey, parentLabel, menuKey, menuLabel }
    const childIdToInfo = new Map<string, { simpleKey: string; label: string; parentSimpleKey: string; parentLabel: string; menuKey: string; menuLabel: string }>();
    // "menuKey_featureKey" -> feature ID (for loading saved data)
    const menuFeatureKeyToId = new Map<string, string>();
    // "menuKey_featureKey_childKey" -> child ID
    const menuChildKeyToId = new Map<string, string>();

    if (!filteredPermissionsHierarchy) return { featureIdToInfo, childIdToInfo, menuFeatureKeyToId, menuChildKeyToId };

    // Helper to get actual menu path key from label using allowedMenus lookup
    const getActualMenuKey = (label: string, fallbackPath: string): string => {
      const normalizedLabel = label.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and').replace(/-/g, '_');
      // Try to find actual path from allowedMenus
      const actualPath = buildMenuLabelToPathMap.get(normalizedLabel) || buildMenuLabelToPathMap.get(label.toLowerCase());
      if (actualPath) {
        console.log(`[buildLookupMaps] Found actual path for "${label}": ${actualPath}`);
        return actualPath;
      }
      // Fallback to hierarchy path
      const fallbackKey = fallbackPath.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_');
      console.log(`[buildLookupMaps] Using fallback path for "${label}": ${fallbackKey}`);
      return fallbackKey;
    };

    filteredPermissionsHierarchy.forEach(menu => {
      // Get menuKey using actual menu path from allowedMenus (or fallback to hierarchy path)
      const menuKey = getActualMenuKey(menu.label, menu.path);
      const menuLabel = menu.label;
      const menuBase = menu.label.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and').replace(/[^a-z0-9_]/g, '');

      // Process parent menu features
      menu.features?.forEach(feature => {
        const simpleKey = feature.id.replace(new RegExp(`_${menuBase}$`), '');

        featureIdToInfo.set(feature.id, { simpleKey, label: feature.label, menuKey, menuLabel });
        menuFeatureKeyToId.set(`${menuKey}_${simpleKey}`, feature.id);

        feature.children?.forEach(sub => {
          const childSimpleKey = sub.id.replace(new RegExp(`^${simpleKey}_`), '').replace(new RegExp(`_${menuBase}$`), '');
          childIdToInfo.set(sub.id, {
            simpleKey: childSimpleKey,
            label: sub.label,
            parentSimpleKey: simpleKey,
            parentLabel: feature.label,
            menuKey,
            menuLabel
          });
          menuChildKeyToId.set(`${menuKey}_${simpleKey}_${childSimpleKey}`, sub.id);
        });
      });

      // Process child menu features (sub-menus)
      menu.children?.forEach(child => {
        // Get actual menu key for child menu using allowedMenus
        const childMenuKey = getActualMenuKey(child.label, child.path);
        const childMenuLabel = child.label;
        const childMenuBase = child.label.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and').replace(/[^a-z0-9_]/g, '');

        child.features?.forEach(feature => {
          const simpleKey = feature.id.replace(new RegExp(`_${childMenuBase}$`), '');

          featureIdToInfo.set(feature.id, { simpleKey, label: feature.label, menuKey: childMenuKey, menuLabel: childMenuLabel });
          menuFeatureKeyToId.set(`${childMenuKey}_${simpleKey}`, feature.id);

          feature.children?.forEach(sub => {
            const childSimpleKey = sub.id.replace(new RegExp(`^${simpleKey}_`), '').replace(new RegExp(`_${childMenuBase}$`), '');
            childIdToInfo.set(sub.id, {
              simpleKey: childSimpleKey,
              label: sub.label,
              parentSimpleKey: simpleKey,
              parentLabel: feature.label,
              menuKey: childMenuKey,
              menuLabel: childMenuLabel
            });
            menuChildKeyToId.set(`${childMenuKey}_${simpleKey}_${childSimpleKey}`, sub.id);
          });
        });

        // Handle child menus without features (menu itself is the permission)
        if (!child.features || child.features.length === 0) {
          // Use the actual menu key from allowedMenus as both the ID and menuKey
          featureIdToInfo.set(childMenuKey, { simpleKey: 'access', label: childMenuLabel, menuKey: childMenuKey, menuLabel: childMenuLabel });
          menuFeatureKeyToId.set(`${childMenuKey}_access`, childMenuKey);
          console.log(`[buildLookupMaps] Added menu-only permission: ${childMenuKey} -> ${childMenuLabel}`);
        }
      });

      // Handle parent menus without features/children
      if ((!menu.features || menu.features.length === 0) && (!menu.children || menu.children.length === 0)) {
        featureIdToInfo.set(menuKey, { simpleKey: 'access', label: menuLabel, menuKey, menuLabel });
        menuFeatureKeyToId.set(`${menuKey}_access`, menuKey);
      }
    });

    return { featureIdToInfo, childIdToInfo, menuFeatureKeyToId, menuChildKeyToId };
  };

  const { featureIdToInfo, childIdToInfo, menuFeatureKeyToId, menuChildKeyToId } = buildLookupMaps();

  // Convert HierarchicalPermissions (grouped by menu) to flat ID set for UI state
  // New format: { menuPathKey: { label: menuLabel, children: { featureKey: featureLabel } } }
  // Also supports old format for backward compatibility
  const hierarchicalToFlatIds = (perms: HierarchicalPermissions): Set<string> => {
    const ids = new Set<string>();
    if (!perms || typeof perms !== 'object') return ids;

    // Iterate through menus (menuKey is derived from path like analytics_overview)
    for (const [menuKey, menuData] of Object.entries(perms)) {
      if (menuData && typeof menuData === 'object') {
        const typedMenuData = menuData as { label?: string; children?: Record<string, string> };

        // New format: { menuPathKey: { label, children: { featureKey: featureLabel } } }
        if (typedMenuData.label && typedMenuData.children) {
          // Process children which are feature keys
          for (const [featureKey] of Object.entries(typedMenuData.children)) {
            // Try to find feature ID: menuKey_featureKey or just the feature as stored
            const featureId = menuFeatureKeyToId.get(`${menuKey}_${featureKey}`);
            if (featureId) {
              ids.add(featureId);
            } else {
              // Try splitting combined key (parentSimpleKey_childSimpleKey)
              const parts = featureKey.split('_');
              if (parts.length >= 2) {
                const parentKey = parts[0];
                const childKey = parts.slice(1).join('_');
                const childId = menuChildKeyToId.get(`${menuKey}_${parentKey}_${childKey}`);
                if (childId) {
                  ids.add(childId);
                }
              }
            }
          }
        } else {
          // Old format support: { menuKey: { featureKey: { label, children } } }
          for (const [featureKey, featureValue] of Object.entries(menuData)) {
            if (featureValue && typeof featureValue === 'object') {
              const typedFeatureValue = featureValue as unknown as { label?: string; children?: Record<string, string> };
              if (typedFeatureValue.label) {
                const featureId = menuFeatureKeyToId.get(`${menuKey}_${featureKey}`);
                if (featureId) {
                  ids.add(featureId);
                }

                if (typedFeatureValue.children) {
                  for (const childKey of Object.keys(typedFeatureValue.children)) {
                    const childId = menuChildKeyToId.get(`${menuKey}_${featureKey}_${childKey}`);
                    if (childId) {
                      ids.add(childId);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return ids;
  };

  // Convert flat selected IDs to hierarchical structure grouped by menu for saving
  // Output format: { menuPathKey: { label: menuLabel, children: { featureKey: featureLabel } } }
  // menuPathKey is derived from the actual menu path (e.g., /analytics/overview -> analytics_overview)
  // This ensures the saved permission keys match the menu paths for filtering
  const flatIdsToHierarchical = (ids: Set<string>): HierarchicalPermissions => {
    const result: Record<string, { label: string; children?: Record<string, string> }> = {};

    if (!filteredPermissionsHierarchy) return result as unknown as HierarchicalPermissions;

    console.log('[flatIdsToHierarchical] Processing selected IDs:', Array.from(ids));
    console.log('[flatIdsToHierarchical] featureIdToInfo keys:', Array.from(featureIdToInfo.keys()));

    // Process selected IDs and group by menu path
    ids.forEach(id => {
      // Check if this is a sub-feature (child)
      const childInfo = childIdToInfo.get(id);
      if (childInfo) {
        // Use menuKey (derived from path) as the top-level key
        if (!result[childInfo.menuKey]) {
          result[childInfo.menuKey] = {
            label: childInfo.menuLabel,
            children: {}
          };
        }
        if (!result[childInfo.menuKey].children) {
          result[childInfo.menuKey].children = {};
        }
        // Save the sub-feature under children with a combined key
        const childFeatureKey = `${childInfo.parentSimpleKey}_${childInfo.simpleKey}`;
        result[childInfo.menuKey].children![childFeatureKey] = childInfo.label;
        console.log(`[flatIdsToHierarchical] Added child feature: ${childInfo.menuKey}.children.${childFeatureKey}`);
        return;
      }

      // Check if this is a feature (parent) or menu-only permission
      const featureInfo = featureIdToInfo.get(id);
      if (featureInfo) {
        // Use menuKey (derived from path) as the top-level key
        if (!result[featureInfo.menuKey]) {
          result[featureInfo.menuKey] = {
            label: featureInfo.menuLabel,
            children: {}
          };
        }
        if (!result[featureInfo.menuKey].children) {
          result[featureInfo.menuKey].children = {};
        }
        // Add feature to children (for menu-only permissions, simpleKey is 'access')
        result[featureInfo.menuKey].children![featureInfo.simpleKey] = featureInfo.label;
        console.log(`[flatIdsToHierarchical] Added feature: ${featureInfo.menuKey}.children.${featureInfo.simpleKey}`);
      } else {
        console.log(`[flatIdsToHierarchical] ID not found in maps: ${id}`);
      }
    });

    console.log('[flatIdsToHierarchical] Final result:', JSON.stringify(result, null, 2));
    return result as unknown as HierarchicalPermissions;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        permissions: initialData.permissions || {},
        isActive: initialData.isActive,
      });
      setSelectedIds(hierarchicalToFlatIds(initialData.permissions));
    } else {
      setFormData({
        name: '',
        description: '',
        permissions: {},
        isActive: true,
      });
      setSelectedIds(new Set());
    }
    setErrors({});
    // Expand all menus and features with sub-features by default
    if (filteredPermissionsHierarchy && filteredPermissionsHierarchy.length > 0) {
      const allExpandableIds = new Set<string>();
      filteredPermissionsHierarchy.forEach(menu => {
        allExpandableIds.add(menu.id);
        // Add feature IDs that have children (sub-features)
        menu.features?.forEach(feature => {
          if (feature.children && feature.children.length > 0) {
            allExpandableIds.add(feature.id);
          }
        });
        menu.children?.forEach(child => {
          allExpandableIds.add(child.id);
          // Add child menu feature IDs that have children (sub-features)
          child.features?.forEach(feature => {
            if (feature.children && feature.children.length > 0) {
              allExpandableIds.add(feature.id);
            }
          });
        });
      });
      setExpandedMenus(allExpandableIds);
    }
  }, [initialData, open, filteredPermissionsHierarchy]);

  // Check if a permission ID is selected
  const isPermissionSelected = (id: string): boolean => {
    return selectedIds.has(id);
  };

  // Toggle a single permission
  const handlePermissionToggle = (permissionId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const handleCategoryToggle = (category: string) => {
    const categoryPermissions = groupedPermissions[category].map((p) => p.id);
    const allSelected = categoryPermissions.every((p) => selectedIds.has(p));

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        categoryPermissions.forEach(p => newSet.delete(p));
      } else {
        categoryPermissions.forEach(p => newSet.add(p));
      }
      return newSet;
    });
  };

  // Toggle expanded state for a menu
  const toggleMenuExpanded = (menuId: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  // Helper function to get actual menu key from allowedMenus using label
  // Moved here so it can be used by getAllFeatureIds and other functions
  const getActualMenuKeyForToggle = (label: string, fallbackPath: string): string => {
    const normalizedLabel = label.toLowerCase().replace(/\s+/g, '_').replace(/&/g, 'and').replace(/-/g, '_');
    const actualPath = buildMenuLabelToPathMap.get(normalizedLabel) || buildMenuLabelToPathMap.get(label.toLowerCase());
    if (actualPath) {
      return actualPath;
    }
    // Fallback to hierarchy path
    return fallbackPath.replace(/^\//,'').replace(/\//g, '_').replace(/-/g, '_');
  };

  // Get all feature IDs for a menu item (including children and sub-features)
  // Uses actual menu paths from allowedMenus for consistency
  const getAllFeatureIds = (menu: PermissionHierarchyItem | PermissionHierarchyChild): string[] => {
    const ids: string[] = [];
    menu.features?.forEach(f => {
      ids.push(f.id);
      f.children?.forEach(subFeature => ids.push(subFeature.id));
    });
    if ('children' in menu && menu.children) {
      menu.children.forEach(child => {
        if (child.features && child.features.length > 0) {
          child.features.forEach(f => {
            ids.push(f.id);
            f.children?.forEach(subFeature => ids.push(subFeature.id));
          });
        } else {
          // Use actual menu path from allowedMenus instead of permissionsHierarchy path
          const menuPermissionId = getActualMenuKeyForToggle(child.label, child.path);
          ids.push(menuPermissionId);
        }
      });
    }
    return ids;
  };

  // Get all sub-feature IDs for a specific feature
  const getFeatureSubIds = (feature: PermissionFeature): string[] => {
    const ids: string[] = [feature.id];
    feature.children?.forEach(subFeature => ids.push(subFeature.id));
    return ids;
  };

  // Handle toggling all sub-features for a feature
  const handleFeatureToggle = (feature: PermissionFeature) => {
    const allIds = getFeatureSubIds(feature);
    const allSelected = allIds.every(id => selectedIds.has(id));

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        allIds.forEach(id => newSet.delete(id));
      } else {
        allIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handle toggling all features for a parent menu
  const handleMenuToggle = (menu: PermissionHierarchyItem) => {
    const allFeatureIds = getAllFeatureIds(menu);

    if (allFeatureIds.length === 0) {
      // Use actual menu path from allowedMenus instead of permissionsHierarchy path
      const menuPermissionId = getActualMenuKeyForToggle(menu.label, menu.path);
      console.log(`[handleMenuToggle] Menu "${menu.label}" has no features, using menuPermissionId: ${menuPermissionId}`);
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(menuPermissionId)) {
          newSet.delete(menuPermissionId);
        } else {
          newSet.add(menuPermissionId);
        }
        return newSet;
      });
      return;
    }

    const allSelected = allFeatureIds.every(id => selectedIds.has(id));

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        allFeatureIds.forEach(id => newSet.delete(id));
      } else {
        allFeatureIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  // Handle toggling all features for a child menu
  const handleChildMenuToggle = (child: PermissionHierarchyChild) => {
    const featureIds = child.features?.flatMap(f => {
      const ids = [f.id];
      f.children?.forEach(sub => ids.push(sub.id));
      return ids;
    }) || [];

    if (featureIds.length === 0) {
      // Use actual menu path from allowedMenus instead of permissionsHierarchy path
      const menuPermissionId = getActualMenuKeyForToggle(child.label, child.path);
      console.log(`[handleChildMenuToggle] Child "${child.label}" has no features, using menuPermissionId: ${menuPermissionId}`);
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(menuPermissionId)) {
          newSet.delete(menuPermissionId);
        } else {
          newSet.add(menuPermissionId);
        }
        return newSet;
      });
      return;
    }

    const allSelected = featureIds.every(id => selectedIds.has(id));

    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        featureIds.forEach(id => newSet.delete(id));
      } else {
        featureIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Role name must not exceed 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }

    if (selectedIds.size === 0) {
      newErrors.permissions = 'Please select at least one permission';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      // Convert selected IDs to hierarchical structure before submitting
      const hierarchicalPermissions = flatIdsToHierarchical(selectedIds);

      await onSubmit({
        ...formData,
        permissions: hierarchicalPermissions,
      });
      onClose();
    } catch (error: unknown) {
      console.error('Error submitting role:', error);

      let errorMessage = 'Failed to save role. Please try again.';
      const err = error as { message?: string };

      if (err.message) {
        errorMessage = err.message;

        if (err.message.includes('already exists')) {
          setErrors({
            name: `A role with the name "${formData.name}" already exists. Please choose a different name.`,
            submit: errorMessage
          });
        } else {
          setErrors({ submit: errorMessage });
        }
      } else {
        setErrors({ submit: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border-2 border-blue-300 dark:border-blue-700 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>
                {initialData ? 'Edit Role' : 'Create New Role'}
              </DialogTitle>
              <DialogDescription>
                {initialData
                  ? 'Update the role details and permissions'
                  : 'Define a new role with specific permissions'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-1 scrollbar-thin">
            <div className="space-y-6 py-4">
              {/* Role Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Role Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g., Data Analyst, ESG Coordinator"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={errors.name ? 'border-red-500' : ''}
                  disabled={initialData?.isSystem}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
                {initialData?.isSystem && (
                  <p className="text-xs text-muted-foreground">
                    System roles cannot be renamed
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description <span className="text-muted-foreground text-xs">(Optional)</span>
                </Label>
                <Textarea
                  id="description"
                  placeholder="Describe the role's purpose and responsibilities..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className={errors.description ? 'border-red-500' : ''}
                  rows={3}
                />
                {errors.description && (
                  <p className="text-xs text-red-500">{errors.description}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.description?.length || 0}/500 characters
                </p>
              </div>

              {/* Permissions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Role Permissions <span className="text-red-500">*</span>
                  </Label>
                  <Badge variant="outline" className="text-xs">
                    {selectedIds.size} selected
                  </Badge>
                </div>
                {errors.permissions && (
                  <p className="text-xs text-red-500">{errors.permissions}</p>
                )}

                <div className="border border-border rounded-lg p-4 space-y-3">
                  {/* Legend */}
                  {hasHierarchy && (
                    <div className="flex flex-wrap items-center gap-4 pb-3 mb-3 border-b border-border text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-blue-500" />
                        <span>Module</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FolderOpen className="w-3.5 h-3.5 text-purple-500" />
                        <span>Sub-module</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Settings2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span>Feature</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CircleDot className="w-3.5 h-3.5 text-orange-500" />
                        <span>Permission</span>
                      </div>
                    </div>
                  )}

                  {/* Hierarchical Permissions View */}
                  {hasHierarchy ? (
                    filteredPermissionsHierarchy.map((menu) => {
                      const isExpanded = expandedMenus.has(menu.id);
                      const allFeatureIds = getAllFeatureIds(menu);
                      const hasChildren = menu.children && menu.children.length > 0;
                      const hasFeatures = menu.features && menu.features.length > 0;

                      // Use actual menu path from allowedMenus for checkbox state
                      const menuPermissionId = getActualMenuKeyForToggle(menu.label, menu.path);

                      const allSelected = allFeatureIds.length > 0
                        ? allFeatureIds.every(id => selectedIds.has(id))
                        : selectedIds.has(menuPermissionId);
                      const someSelected = allFeatureIds.length > 0
                        ? allFeatureIds.some(id => selectedIds.has(id))
                        : false;

                      return (
                        <div key={menu.id} className="space-y-2">
                          {/* Parent Menu Header - MODULE LEVEL */}
                          <div className="flex items-center gap-2 py-2 px-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                            {(hasChildren || hasFeatures) && (
                              <button
                                type="button"
                                onClick={() => toggleMenuExpanded(menu.id)}
                                className="p-0.5 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                )}
                              </button>
                            )}
                            <Layers className="w-4 h-4 text-blue-500" />
                            <Checkbox
                              id={`menu-${menu.id}`}
                              checked={allSelected}
                              ref={(el) => {
                                if (el && allFeatureIds.length > 0) {
                                  (el as HTMLButtonElement).dataset.indeterminate = String(someSelected && !allSelected);
                                }
                              }}
                              onCheckedChange={() => handleMenuToggle(menu)}
                            />
                            <Label
                              htmlFor={`menu-${menu.id}`}
                              className="text-sm font-semibold cursor-pointer text-blue-700 dark:text-blue-300"
                            >
                              {menu.label}
                            </Label>
                            <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700">
                              Module
                            </Badge>
                          </div>

                          {/* Parent Menu Features & Children */}
                          {isExpanded && (
                            <div className="ml-4 pl-4 border-l-2 border-blue-200 dark:border-blue-800 space-y-3">
                              {/* Parent's own features - FEATURE LEVEL */}
                              {hasFeatures && (
                                <div className="space-y-2">
                                  {menu.features.map((feature) => {
                                    const hasSubFeatures = feature.children && feature.children.length > 0;
                                    const featureExpanded = expandedMenus.has(feature.id);
                                    const featureAllIds = getFeatureSubIds(feature);
                                    const featureAllSelected = featureAllIds.every(id => selectedIds.has(id));
                                    const featureSomeSelected = featureAllIds.some(id => selectedIds.has(id));

                                    return (
                                      <div key={feature.id} className="space-y-1">
                                        <div className="flex items-center gap-2 py-1.5 px-2 bg-emerald-50 dark:bg-emerald-950/30 rounded border border-emerald-200 dark:border-emerald-800">
                                          {hasSubFeatures && (
                                            <button
                                              type="button"
                                              onClick={() => toggleMenuExpanded(feature.id)}
                                              className="p-0.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded"
                                            >
                                              {featureExpanded ? (
                                                <ChevronDown className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                              ) : (
                                                <ChevronRight className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                              )}
                                            </button>
                                          )}
                                          <Settings2 className="w-3.5 h-3.5 text-emerald-500" />
                                          <Checkbox
                                            id={feature.id}
                                            checked={hasSubFeatures ? featureAllSelected : isPermissionSelected(feature.id)}
                                            ref={(el) => {
                                              if (el && hasSubFeatures) {
                                                (el as HTMLButtonElement).dataset.indeterminate = String(featureSomeSelected && !featureAllSelected);
                                              }
                                            }}
                                            onCheckedChange={() => hasSubFeatures ? handleFeatureToggle(feature) : handlePermissionToggle(feature.id)}
                                          />
                                          <Label
                                            htmlFor={feature.id}
                                            className={`text-sm cursor-pointer ${hasSubFeatures ? 'font-medium text-emerald-700 dark:text-emerald-300' : 'text-emerald-600 dark:text-emerald-400'}`}
                                          >
                                            {feature.label}
                                          </Label>
                                          {hasSubFeatures && (
                                            <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700">
                                              Feature
                                            </Badge>
                                          )}
                                        </div>

                                        {/* Sub-features for parent menu features - PERMISSION LEVEL */}
                                        {hasSubFeatures && featureExpanded && (
                                          <div className="ml-4 pl-4 border-l-2 border-emerald-200 dark:border-emerald-800">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 py-2">
                                              {feature.children!.map((subFeature) => (
                                                <div key={subFeature.id} className="flex items-center gap-2 py-1 px-2 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800/50">
                                                  <CircleDot className="w-3 h-3 text-orange-500" />
                                                  <Checkbox
                                                    id={subFeature.id}
                                                    checked={isPermissionSelected(subFeature.id)}
                                                    onCheckedChange={() => handlePermissionToggle(subFeature.id)}
                                                  />
                                                  <Label
                                                    htmlFor={subFeature.id}
                                                    className="text-sm cursor-pointer text-orange-700 dark:text-orange-400"
                                                  >
                                                    {subFeature.label}
                                                  </Label>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Child Menus - SUB-MODULE LEVEL */}
                              {hasChildren && menu.children.map((child) => {
                                const childExpanded = expandedMenus.has(child.id);
                                const childHasFeatures = child.features && child.features.length > 0;

                                const childAllFeatureIds = child.features?.flatMap(f => {
                                  const ids = [f.id];
                                  f.children?.forEach(sub => ids.push(sub.id));
                                  return ids;
                                }) || [];

                                // Use actual menu path from allowedMenus for checkbox state
                                const childMenuPermissionId = getActualMenuKeyForToggle(child.label, child.path);

                                const childAllSelected = childHasFeatures
                                  ? childAllFeatureIds.length > 0 && childAllFeatureIds.every(id => selectedIds.has(id))
                                  : selectedIds.has(childMenuPermissionId);
                                const childSomeSelected = childHasFeatures
                                  ? childAllFeatureIds.some(id => selectedIds.has(id))
                                  : false;

                                return (
                                  <div key={child.id} className="space-y-2">
                                    {/* Child Menu Header - SUB-MODULE */}
                                    <div className="flex items-center gap-2 py-1.5 px-2 bg-purple-50 dark:bg-purple-950/30 rounded border border-purple-200 dark:border-purple-800">
                                      {childHasFeatures && (
                                        <button
                                          type="button"
                                          onClick={() => toggleMenuExpanded(child.id)}
                                          className="p-0.5 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded"
                                        >
                                          {childExpanded ? (
                                            <ChevronDown className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                          ) : (
                                            <ChevronRight className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                          )}
                                        </button>
                                      )}
                                      <FolderOpen className="w-3.5 h-3.5 text-purple-500" />
                                      <Checkbox
                                        id={`child-${child.id}`}
                                        checked={childAllSelected}
                                        ref={(el) => {
                                          if (el && childHasFeatures) {
                                            (el as HTMLButtonElement).dataset.indeterminate = String(childSomeSelected && !childAllSelected);
                                          }
                                        }}
                                        onCheckedChange={() => handleChildMenuToggle(child)}
                                      />
                                      <Label
                                        htmlFor={`child-${child.id}`}
                                        className="text-sm font-medium cursor-pointer text-purple-700 dark:text-purple-300"
                                      >
                                        {child.label}
                                      </Label>
                                      <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-700">
                                        Sub-module
                                      </Badge>
                                    </div>

                                    {/* Child's Features - FEATURE LEVEL */}
                                    {childExpanded && childHasFeatures && (
                                      <div className="ml-4 pl-4 border-l-2 border-purple-200 dark:border-purple-800 space-y-2">
                                        {child.features.map((feature) => {
                                          const hasSubFeatures = feature.children && feature.children.length > 0;
                                          const featureExpanded = expandedMenus.has(feature.id);
                                          const featureAllIds = getFeatureSubIds(feature);
                                          const featureAllSelected = featureAllIds.every(id => selectedIds.has(id));
                                          const featureSomeSelected = featureAllIds.some(id => selectedIds.has(id));

                                          return (
                                            <div key={feature.id} className="space-y-1">
                                              <div className="flex items-center gap-2 py-1.5 px-2 bg-emerald-50 dark:bg-emerald-950/30 rounded border border-emerald-200 dark:border-emerald-800">
                                                {hasSubFeatures && (
                                                  <button
                                                    type="button"
                                                    onClick={() => toggleMenuExpanded(feature.id)}
                                                    className="p-0.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded"
                                                  >
                                                    {featureExpanded ? (
                                                      <ChevronDown className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                                    ) : (
                                                      <ChevronRight className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                                                    )}
                                                  </button>
                                                )}
                                                <Settings2 className="w-3.5 h-3.5 text-emerald-500" />
                                                <Checkbox
                                                  id={feature.id}
                                                  checked={hasSubFeatures ? featureAllSelected : isPermissionSelected(feature.id)}
                                                  ref={(el) => {
                                                    if (el && hasSubFeatures) {
                                                      (el as HTMLButtonElement).dataset.indeterminate = String(featureSomeSelected && !featureAllSelected);
                                                    }
                                                  }}
                                                  onCheckedChange={() => hasSubFeatures ? handleFeatureToggle(feature) : handlePermissionToggle(feature.id)}
                                                />
                                                <Label
                                                  htmlFor={feature.id}
                                                  className={`text-sm cursor-pointer ${hasSubFeatures ? 'font-medium text-emerald-700 dark:text-emerald-300' : 'text-emerald-600 dark:text-emerald-400'}`}
                                                >
                                                  {feature.label}
                                                </Label>
                                                {hasSubFeatures && (
                                                  <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700">
                                                    Feature
                                                  </Badge>
                                                )}
                                              </div>

                                              {/* Sub-features - PERMISSION LEVEL */}
                                              {hasSubFeatures && featureExpanded && (
                                                <div className="ml-4 pl-4 border-l-2 border-emerald-200 dark:border-emerald-800">
                                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 py-2">
                                                    {feature.children!.map((subFeature) => (
                                                      <div key={subFeature.id} className="flex items-center gap-2 py-1 px-2 bg-orange-50 dark:bg-orange-950/20 rounded border border-orange-200 dark:border-orange-800/50">
                                                        <CircleDot className="w-3 h-3 text-orange-500" />
                                                        <Checkbox
                                                          id={subFeature.id}
                                                          checked={isPermissionSelected(subFeature.id)}
                                                          onCheckedChange={() => handlePermissionToggle(subFeature.id)}
                                                        />
                                                        <Label
                                                          htmlFor={subFeature.id}
                                                          className="text-sm cursor-pointer text-orange-700 dark:text-orange-400"
                                                        >
                                                          {subFeature.label}
                                                        </Label>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    /* Fallback: Flat Permissions View (grouped by category) */
                    Object.entries(groupedPermissions).map(([category, permissions]) => {
                      const categoryPermissionIds = permissions.map((p) => p.id);
                      const allSelected = categoryPermissionIds.every((p) => selectedIds.has(p));

                      return (
                        <div key={category} className="space-y-3">
                          <div className="flex items-center gap-2 pb-2 border-b border-border">
                            <Checkbox
                              id={`category-${category}`}
                              checked={allSelected}
                              onCheckedChange={() => handleCategoryToggle(category)}
                            />
                            <Label
                              htmlFor={`category-${category}`}
                              className="text-sm font-semibold capitalize cursor-pointer"
                            >
                              {category.replace(/_/g, ' ')}
                            </Label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                            {permissions.map((permission) => (
                              <div key={permission.id} className="flex items-center gap-2">
                                <Checkbox
                                  id={permission.id}
                                  checked={isPermissionSelected(permission.id)}
                                  onCheckedChange={() =>
                                    handlePermissionToggle(permission.id)
                                  }
                                />
                                <Label
                                  htmlFor={permission.id}
                                  className="text-sm cursor-pointer"
                                >
                                  {permission.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked as boolean })
                  }
                />
                <Label htmlFor="isActive" className="text-sm cursor-pointer">
                  Active role (users can be assigned to this role)
                </Label>
              </div>

              {errors.submit && (
                <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {errors.submit}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-6 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {initialData ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{initialData ? 'Update Role' : 'Create Role'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RoleFormModal;
